import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  emptyTally,
  isPollEvent,
  isResidentVote,
  isVoteEvent,
  loadMyEvents,
  normalizeChoice,
  parseEvent,
  POLL_KIND,
  POLL_RESPONSE_KIND,
  rememberMyEvent,
  voteChoice,
  voteJurisdiction,
  votePollId,
} from '../utils/nostr.js'
import { POLL_TAG } from '../utils/constants.js'
import { fetchPrimalPollVotes } from '../utils/primalCache.js'

// Fetch poll events tagged #ThePeopleVote, then subscribe to vote replies.
//
// Vote tallying:
//   We keep a per-voter index instead of incrementing counters, because the
//   relays are append-only — when a user changes their vote, both events
//   live on disk forever. We dedupe by (pollId, voterPubkey) and keep only
//   the latest event by created_at. Tallies are then derived from the
//   index, so they always reflect each voter's most recent choice.
export function useFeed({ pool, subscribe, userJurisdiction, userPubkey }) {
  const [items, setItems] = useState([])
  // voteIndex: { [pollId]: { [voterPubkey]: { choice, createdAt, eventId, jurisdiction } } }
  const [voteIndex, setVoteIndex] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // UX43: pull-to-refresh. Bumping the nonce re-triggers the poll
  // subscription useEffect so stale WS connections are replaced with
  // a fresh REQ. Items + voteIndex are kept across refresh — the
  // dedup in both subscription handlers quietly re-receives known
  // events, and anything new slides in without a feed flash.
  const [reloadNonce, setReloadNonce] = useState(0)

  const itemsRef = useRef(items)
  itemsRef.current = items

  // ---------- poll subscription ----------
  useEffect(() => {
    if (!subscribe) return
    setLoading(true)
    setError(null)

    const seen = new Set()
    let received = 0
    let parsedCount = 0
    console.info(`[Verdant] subscribing to bulk #${POLL_TAG} (limit 200)`)
    const sub = subscribe(
      [{ kinds: [1, POLL_KIND], '#t': [POLL_TAG], limit: 200 }],
      (event) => {
        if (seen.has(event.id)) return
        seen.add(event.id)
        received += 1
        const parsed = parseEvent(event)
        if (!parsed) return
        parsedCount += 1
        setItems((prev) => {
          if (prev.find((p) => p.id === parsed.id)) return prev
          const next = [...prev, parsed]
          next.sort((a, b) => b.postedAt - a.postedAt)
          return next
        })
      },
      () => {
        console.info(
          `[Verdant] EOSE for bulk #${POLL_TAG} — received ${received} unique event(s), ${parsedCount} parsed as polls`
        )
        setLoading(false)
      }
    )
    const t = setTimeout(() => setLoading(false), 8000)
    return () => {
      clearTimeout(t)
      try { sub?.close?.() } catch { /* noop */ }
    }
  }, [subscribe, reloadNonce])

  // ---------- vote subscription ----------
  // Records every vote event into the per-voter index, with last-write-wins
  // by created_at.
  const indexVoteEvent = useCallback((event) => {
    const pId = votePollId(event)
    // Poll lookup lets kind 1018 responses from other clients (option id
    // only) resolve to a label. Votes only subscribe once polls are known.
    const choice = voteChoice(event, itemsRef.current.find((p) => p.id === pId))
    if (!pId || !choice) return
    // Don't gate on the poll being known — votes for unknown polls just
    // sit in the index harmlessly and become live the moment the poll
    // arrives (the tally derivation joins on items).

    setVoteIndex((prev) => {
      const pollVotes = prev[pId] ? { ...prev[pId] } : {}
      const existing = pollVotes[event.pubkey]
      if (existing && existing.createdAt >= event.created_at) return prev
      pollVotes[event.pubkey] = {
        choice,
        createdAt: event.created_at,
        eventId: event.id,
        jurisdiction: voteJurisdiction(event),
      }
      return { ...prev, [pId]: pollVotes }
    })
  }, [])

  const pollIds = useMemo(() => items.map((i) => i.id), [items])
  const pollIdsKey = pollIds.join('|')

  useEffect(() => {
    if (!subscribe || !pollIds.length) return
    const seenVotes = new Set()
    const sub = subscribe(
      [{ kinds: [1, POLL_RESPONSE_KIND], '#e': pollIds }],
      (event) => {
        if (seenVotes.has(event.id)) return
        seenVotes.add(event.id)
        indexVoteEvent(event)
      }
    )
    return () => { try { sub?.close?.() } catch { /* noop */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollIdsKey, subscribe])

  // ---------- Primal cache votes ----------
  // Primal's web app sends poll votes only to Primal's cache, not to
  // relays, so read NIP-88 polls' votes from there too. Each poll is
  // fetched once per page load; the short delay batches the burst of
  // polls arriving from the bulk subscription into one connection.
  const primalFetchedRef = useRef(new Set())
  const nip88PollIdsKey = useMemo(
    () => items.filter((i) => i.nostrKind === POLL_KIND).map((i) => i.id).join('|'),
    [items]
  )

  useEffect(() => {
    const ids = nip88PollIdsKey
      ? nip88PollIdsKey.split('|').filter((id) => !primalFetchedRef.current.has(id))
      : []
    if (!ids.length) return
    let cancel = () => {}
    const t = setTimeout(() => {
      console.info(`[Verdant] reading Primal cache votes for ${ids.length} poll(s)`)
      cancel = fetchPrimalPollVotes(ids, {
        onVote: indexVoteEvent,
        onPollDone: (id) => primalFetchedRef.current.add(id),
      })
    }, 1500)
    return () => { clearTimeout(t); cancel() }
  }, [nip88PollIdsKey, indexVoteEvent])

  // ---------- by-author subscription ----------
  // The bulk #ThePeopleVote subscription is limited to the 200 most recent
  // events, which can bury a user's own posts under bot traffic. This
  // separate subscription fetches everything authored by the logged-in
  // pubkey so the user always sees their own polls and votes — and we
  // persist them to localStorage as they arrive.
  useEffect(() => {
    if (!subscribe || !userPubkey) return
    const short = userPubkey.slice(0, 12) + '…'
    console.info(`[Verdant] subscribing to events authored by ${short}`)
    const seenMine = new Set()
    let count = 0
    const sub = subscribe(
      [{ kinds: [1, POLL_KIND, POLL_RESPONSE_KIND], authors: [userPubkey] }],
      (event) => {
        if (seenMine.has(event.id)) return
        seenMine.add(event.id)
        count += 1
        const isPoll = isPollEvent(event)
        const isVote = isVoteEvent(event)
        console.info(
          `[Verdant] own event #${count}`,
          event.id.slice(0, 8),
          isPoll ? 'POLL' : isVote ? 'VOTE' : 'OTHER',
          new Date(event.created_at * 1000).toISOString()
        )
        if (isPoll) {
          const parsed = parseEvent(event)
          if (parsed) {
            setItems((prev) => {
              if (prev.find((p) => p.id === parsed.id)) return prev
              const next = [...prev, parsed]
              next.sort((a, b) => b.postedAt - a.postedAt)
              return next
            })
            rememberMyEvent(event)
          }
        } else if (isVote) {
          indexVoteEvent(event)
          rememberMyEvent(event)
        }
      },
      () => {
        // EOSE: relays report end-of-stored-events. If count is 0, no
        // relay had any historical events for this pubkey.
        console.info(
          `[Verdant] EOSE for own-events query — ${count} event${count === 1 ? '' : 's'} returned for ${short}`
        )
      }
    )
    return () => { try { sub?.close?.() } catch { /* noop */ } }
  }, [subscribe, userPubkey, indexVoteEvent])

  // ---------- derive tallies ----------
  const tallies = useMemo(() => {
    const out = {}
    for (const pollId of Object.keys(voteIndex)) {
      const poll = items.find((p) => p.id === pollId)
      if (!poll) continue
      const options = poll.kind === 'candidate'
        ? poll.candidates.map((c) => c.name)
        : poll.options
      const tally = emptyTally(options)
      const votes = voteIndex[pollId] || {}
      for (const voterPubkey of Object.keys(votes)) {
        const v = votes[voterPubkey]
        // Normalize historic Aye/Nay vote events into Yes/No so tallies for
        // pre-rename polls don't fall into orphan buckets.
        const normalized = normalizeChoice(v.choice)
        const key = options.find(
          (o) => o.toLowerCase() === normalized.toLowerCase()
        ) || normalized
        if (!tally[key]) tally[key] = { resident: 0, nonResident: 0 }
        const resident = isResidentVote(v.jurisdiction, userJurisdiction, poll.jurisdiction)
        if (resident) tally[key].resident += 1
        else tally[key].nonResident += 1
      }
      out[pollId] = tally
    }
    return out
  }, [voteIndex, items, userJurisdiction])

  // ---------- optimistic local insertion ----------
  const addLocalEvent = useCallback((rawEvent) => {
    if (!rawEvent) return
    const parsed = parseEvent(rawEvent)
    if (!parsed) return
    setItems((prev) => {
      if (prev.find((p) => p.id === parsed.id)) return prev
      const next = [parsed, ...prev]
      next.sort((a, b) => b.postedAt - a.postedAt)
      return next
    })
  }, [])

  const addLocalVote = useCallback((rawEvent) => {
    if (!rawEvent || !isVoteEvent(rawEvent)) return
    indexVoteEvent(rawEvent)
  }, [indexVoteEvent])

  // ---------- hydrate from localStorage ----------
  // The user's own signed events live in localStorage so they persist
  // across reloads even when relays drop them. Replay them on first render
  // so the user always sees what they posted/voted. (Placed after
  // indexVoteEvent so the const is initialized when this effect runs.)
  useEffect(() => {
    const cached = loadMyEvents()
    if (!cached.length) return
    const polls = []
    for (const ev of cached) {
      if (isPollEvent(ev)) {
        const parsed = parseEvent(ev)
        if (parsed) polls.push(parsed)
      } else if (isVoteEvent(ev)) {
        indexVoteEvent(ev)
      }
    }
    if (polls.length) {
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id))
        const merged = [...prev]
        for (const p of polls) {
          if (!seen.has(p.id)) merged.push(p)
        }
        merged.sort((a, b) => b.postedAt - a.postedAt)
        return merged
      })
    }
  }, [indexVoteEvent])

  // UX43: pull-to-refresh entry point. Bumps the nonce; existing
  // items + votes are retained (dedup handles the re-receive) so
  // there is no feed flash while the fresh subscription is building.
  const refresh = useCallback(() => {
    setReloadNonce((n) => n + 1)
  }, [])

  return { items, tallies, loading, error, voteIndex, addLocalEvent, addLocalVote, refresh }
}
